import { Quaternion, Vector3 } from 'three'
import type { ColorType } from '../core/enums'
import type { QueueSpec } from '../core/levelData'
import { QUEUE_PATH, queueMaxVisible, type GameplayTuning } from './tuning'

/**
 * Port of `PeopleQueue` (TypeDefIndex 8582).
 *
 * A line of passengers that flows continuously toward its mouth. The path is a
 * Catmull-Rom curve through `pathPoints` — index 0 is the mouth — and people
 * stand in `laneCount` lanes side by side, one row every `spacing` units of arc.
 *
 * Two rules matter for gameplay and both were missing before:
 *
 *  - **Only the front leaves.** `TakeFront()` is the only way out, and
 *    `IsFrontReady()` gates it on the leading row having actually arrived
 *    (`entry.Distance <= 0.15`). A passenger further back cannot be picked out
 *    because their colour happens to be wanted.
 *  - **The queue never feeds a bus directly.** It feeds the `WaitingArea`.
 *
 * Queue people are the one kind of character with no wobble: `RetargetEntry`
 * calls `SetAnimationState(Walking)`, which is a no-op stub, and never
 * `WalkAnimation`. They slide along the path and face down it, nothing more.
 */

export interface QueueEntry {
  color: ColorType
  /** Arc distance from the mouth. 0 is the front. */
  distance: number
  /** Where `RetargetEntry` is easing them to — `spacing * Slot`. */
  targetDistance: number
  /** Distance the current retarget started from, for the ease. */
  fromDistance: number
  /** Seconds elapsed / total for the current retarget. `total <= 0` means idle. */
  elapsed: number
  duration: number
  /** Row index down the path. Lane is fixed for life; slot shifts as rows leave. */
  slot: number
  lane: number
}

/** `entry.Distance <= 0.15` in `ReleaseFromTrays`. */
const FRONT_READY_DISTANCE = 0.15

/**
 * 🔍 `UnityEngine.Mathf.Epsilon`, the floor term of `Mathf.Approximately`.
 *
 * `PeopleQueue$$RetargetEntry__0x2120038.c` reads it through the class's static
 * block rather than as a literal:
 *
 *     fVar9 = **(float **)(DAT_06f94708 + 0xb8) * 8.0;   // Mathf.Epsilon * 8
 *     fVar8 = max(max(|a|, |b|) * 1e-06, fVar9);
 *     if (|b - a| < fVar8) return;
 *
 * so the constant is `Mathf.Epsilon`, **not** `FLT_EPSILON`. The two are 31
 * orders of magnitude apart and this port had the wrong one — see
 * `approximately` below for what that cost.
 *
 * ❓ PLACEHOLDER on *which* `Mathf.Epsilon`: Unity picks it at startup,
 * `IsFlushToZeroEnabled ? FloatMinNormal (1.17549435e-38) : FloatMinDenormal
 * (1.401298e-45)`, and the dump carries the field, not the branch that filled
 * it. Settling it would need the FPCR the shipped ARM64 build runs under.
 *
 * It does not matter, and that is measured rather than asserted: the two
 * candidates cross the other term at `max(|a|,|b|) ≈ 9.4e-32`, and over the
 * **1 241 786** `Approximately` calls the 40 shipped levels make, they disagree
 * **0** times. The denormal value is used because it is the documented default.
 */
const MATHF_EPSILON = 1.401298e-45

/**
 * `Mathf.Approximately`, which `RetargetEntry` early-outs on.
 *
 * Exported for `peopleQueue.test.ts`: the two readings differ only on inputs
 * a behaviour test cannot aim at, so the constant needs a test of its own.
 */
export function approximately(a: number, b: number): boolean {
  return Math.abs(b - a) < Math.max(1e-6 * Math.max(Math.abs(a), Math.abs(b)), 8 * MATHF_EPSILON)
}

export class PeopleQueueRuntime {
  readonly index: number
  readonly position: Vector3
  readonly rotation: Quaternion
  readonly mirrored: boolean

  /** Everyone still in this queue, front first. */
  readonly entries: QueueEntry[] = []
  /** Not yet spawned into the visible line. */
  private pending: ColorType[]

  private readonly path: Vector3[] = []
  private readonly cumulative: number[] = []
  private _pathLength = 0

  /** `PeopleQueue._pathLength` — arc length of the sampled path. */
  get pathLength(): number {
    return this._pathLength
  }

  constructor(
    index: number,
    spec: QueueSpec,
    private tuning: GameplayTuning,
  ) {
    this.index = index
    this.position = spec.position.clone()
    this.rotation = spec.rotation.clone()
    this.mirrored = spec.mirrored
    this.pending = [...spec.people]
    this.samplePath()
    // `Initialize` inlines `FillFromPending(true)`: the opening line is placed,
    // not walked in.
    this.fillFromPending(true)
  }

  get totalPeopleLeft(): number {
    return this.entries.length + this.pending.length
  }

  /**
   * The next `limit` colours still to leave this queue, front first.
   *
   * A player can see the line; a planner needs the same view to get a bus onto
   * the road before its colour reaches the waiting area.
   */
  upcomingColors(limit: number): ColorType[] {
    const out: ColorType[] = []
    for (const entry of this.entries) {
      if (out.length >= limit) return out
      out.push(entry.color)
    }
    for (const color of this.pending) {
      if (out.length >= limit) return out
      out.push(color)
    }
    return out
  }

  get isEmpty(): boolean {
    return this.totalPeopleLeft === 0
  }

  get laneCount(): number {
    return this.tuning.queueLaneCount
  }

  /** `PeopleQueue.MaxVisible` — computed, not serialized. */
  get maxVisible(): number {
    return queueMaxVisible(this.pathLength, this.tuning.queueRowSpacing, this.laneCount)
  }

  /**
   * `PeopleQueue.EnsurePathSampled` — Catmull-Rom through the authored points.
   *
   * Mirroring happens **here**, on the path itself:
   *
   *     Vector3 p = pathPoints[i].localPosition;
   *     pts.Add(new Vector3(_mirrored ? -p.x : p.x, p.y, p.z));
   *
   * not on the lane offsets, which is where this port used to apply it. The
   * authored path swings out to +X, so a mirrored queue is the same line
   * reflected about the queue's own Z axis — and the lane order reflects with
   * it, which negating the lateral offset instead does not reproduce.
   */
  private samplePath(): void {
    const flip = this.mirrored ? -1 : 1
    const points = QUEUE_PATH.map((p) => new Vector3(p.x * flip, p.y, p.z))
    const samples = Math.max(1, this.tuning.queueSamplesPerSegment)
    const at = (i: number) => points[Math.max(0, Math.min(points.length - 1, i))]

    this.path.push(points[0].clone())
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = at(i - 1)
      const p1 = at(i)
      const p2 = at(i + 1)
      const p3 = at(i + 2)
      for (let s = 1; s <= samples; s++) {
        this.path.push(catmullRom(p0, p1, p2, p3, s / samples))
      }
    }

    this.cumulative.push(0)
    for (let i = 1; i < this.path.length; i++) {
      this.cumulative.push(this.cumulative[i - 1] + this.path[i].distanceTo(this.path[i - 1]))
    }
    this._pathLength = this.cumulative[this.cumulative.length - 1]
  }

  /**
   * `PeopleQueue.EvaluateLocalPosition` (RVA 0x21206AC) — local position at an
   * arc distance from the mouth.
   *
   * 🔍 Past the end of the path it **extrapolates**, it does not clamp. The
   * decomp's outer test is
   *
   *     if (distance <= _pathLength || _points == null || _points.Count < 2) {
   *         ... clamp to [0, _pathLength] and interpolate ...
   *     } else {
   *         last = _points[Count - 1];
   *         prev = _points[Count - 2];        // ← reads BOTH ends of the tail
   *         ... (last - prev).normalized, with Vector3.normalized's 1e-05 cutoff
   *     }
   *
   * and the `else` arm is the whole point: it fetches `Count - 2` as well as
   * `Count - 1`, which only makes sense as a tangent. The `SQRT(...) <= 1e-05`
   * a few lines down is `Vector3.normalized`'s own zero-length cutoff inlined,
   * so a degenerate tail collapses the arm back to `last`.
   *
   * This is a **standing** branch, not an edge case. `FillFromPending(false)`
   * spawns a replacement at `Max(_pathLength, behind)`, which is past the end
   * by construction. Measured over the 40 levels before this fix: 7 301 of
   * 299 600 entry samples stood beyond `_pathLength`, up to 5.281 units past
   * it, and **93.4 %** of them came back with a zero walk direction — because
   * clamping puts two samples 0.5 apart on the same point. A zero direction
   * costs them both their `LookRotation` and their lane offset, so they
   * collapse onto the path centreline: 11 people on one spot at the worst.
   *
   * ❓ PLACEHOLDER on the **scale**: Ghidra drops the closing addition of a
   * struct-returning function, so `last + dir * (distance - _pathLength)` is
   * read off the shape of the arm (it is the only quantity in scope that makes
   * the two branches meet continuously at `distance == _pathLength`) rather
   * than off an instruction. To settle it: a second decompiler, or a frame of
   * `reference/gameplay.mp4` showing the spacing of the last two rows of a
   * queue that is longer than its path.
   */
  evaluateLocal(distance: number, out = new Vector3()): Vector3 {
    if (distance > this._pathLength && this.path.length >= 2) {
      const last = this.path[this.path.length - 1]
      const prev = this.path[this.path.length - 2]
      out.subVectors(last, prev)
      const length = out.length()
      // `Vector3.normalized` returns zero below 1e-05, which makes the arm
      // degenerate to `last` — the same answer the clamp would have given.
      if (length <= 1e-5) return out.copy(last)
      return out.divideScalar(length).multiplyScalar(distance - this._pathLength).add(last)
    }
    const d = Math.max(0, Math.min(this.pathLength, distance))
    let lo = 0
    let hi = this.cumulative.length - 1
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1
      if (this.cumulative[mid] <= d) lo = mid
      else hi = mid
    }
    const span = this.cumulative[hi] - this.cumulative[lo]
    const t = span > 1e-6 ? (d - this.cumulative[lo]) / span : 0
    return out.copy(this.path[lo]).lerp(this.path[hi], t)
  }

  /**
   * `PeopleQueue.EvaluateWalkDirection` — which way this point of the line faces.
   *
   *     Vector3 a = EvaluateLocalPosition(Mathf.Max(0f, distance - 0.25f));
   *     Vector3 b = EvaluateLocalPosition(distance + 0.25f);
   *     Vector3 d = a - b;
   *     return d.magnitude > 1e-5f ? d.normalized : Vector3.zero;
   *
   * `a` is the sample nearer the mouth, so the vector points **forward down the
   * queue** — toward where people are heading.
   */
  walkDirection(distance: number, out = new Vector3()): Vector3 {
    this.evaluateLocal(Math.max(0, distance - 0.25), _ahead)
    this.evaluateLocal(distance + 0.25, _behind)
    out.subVectors(_ahead, _behind)
    const length = out.length()
    if (length <= 1e-5) return out.set(0, 0, 0)
    return out.divideScalar(length)
  }

  /**
   * `PeopleQueue.ApplyPose` — where one entry stands and which way it faces, in
   * the queue's own local space.
   *
   *     Vector3 pos = EvaluateLocalPosition(entry.Distance);
   *     if (laneCount > 1) {
   *         Vector3 dir = EvaluateWalkDirection(entry.Distance);
   *         if (dir.sqrMagnitude > 0.0001f) {
   *             Vector3 right = Vector3.Cross(Vector3.up, dir).normalized;
   *             pos += right * (laneSpacing * (entry.Lane - (laneCount - 1) * 0.5f));
   *         }
   *     }
   *     t.localPosition = pos;
   *     Vector3 look = EvaluateWalkDirection(entry.Distance);
   *     if (look.sqrMagnitude > 0.0001f) t.localRotation = Quaternion.LookRotation(look);
   *
   * `Cross(up, forward)` is Unity's right vector, so lane 0 is on the left of
   * the direction of travel.
   */
  localPose(
    entry: QueueEntry,
    out: { position: Vector3; forward: Vector3 } = {
      position: new Vector3(),
      forward: new Vector3(),
    },
  ): typeof out {
    this.evaluateLocal(entry.distance, out.position)
    this.walkDirection(entry.distance, out.forward)

    const lanes = this.laneCount
    if (lanes > 1 && out.forward.lengthSq() > 0.0001) {
      // Vector3.Cross(Vector3.up, dir) with up = (0, 1, 0) is (dir.z, 0, -dir.x).
      _right.set(out.forward.z, 0, -out.forward.x)
      const length = _right.length()
      if (length > 1e-5) {
        _right.divideScalar(length)
        const lateral = this.tuning.queueLaneSpacing * (entry.lane - (lanes - 1) * 0.5)
        out.position.addScaledVector(_right, lateral)
      }
    }
    return out
  }

  /** The same pose in Unity world space. */
  worldPosition(entry: QueueEntry, out = new Vector3()): Vector3 {
    this.localPose(entry, _pose)
    return out.copy(_pose.position).applyQuaternion(this.rotation).add(this.position)
  }

  /** `PeopleQueue.PeekFrontColor` — `ColorType.None` when empty. */
  peekFrontColor(): ColorType | null {
    return this.entries.length > 0 ? this.entries[0].color : null
  }

  /** `PeopleQueue.IsFrontReady` — the leading row has finished walking up. */
  isFrontReady(): boolean {
    return this.entries.length > 0 && this.entries[0].distance <= FRONT_READY_DISTANCE
  }

  /**
   * `PeopleQueue.TakeFront` — the only way anyone leaves.
   *
   *     var entry = _entries[0]; int slot = entry.Slot;
   *     _entries.RemoveAt(0);
   *     entry.Tween?.Kill();  character.transform.DOKill();  SetParent(null, true);
   *     if (!_entries.Any(e => e.Slot == slot))
   *         foreach (var e in _entries) e.Slot--;
   *     AdvanceLine();
   *     FillFromPending(false);
   *
   * The row only shuffles up once it is **empty** — three people share a slot,
   * and the line does not step forward until the last of them has gone. Lanes
   * are never reassigned; a passenger keeps the lane they spawned into.
   *
   * Returns the departing entry so the caller can hand its position to
   * `WaitingArea.Receive`, which walks the character on from where it stood.
   */
  takeFront(): QueueEntry | null {
    if (this.entries.length === 0) return null
    const entry = this.entries.shift()!
    const slot = entry.slot
    if (!this.entries.some((e) => e.slot === slot)) {
      for (const e of this.entries) e.slot--
    }
    this.advanceLine()
    // `FillFromPending(false)` — the replacement walks in from the back.
    this.fillFromPending(false)
    return entry
  }

  /**
   * `PeopleQueue.AdvanceLine`:
   *
   *     for (int i = 0; i < _entries.Count; i++)
   *         RetargetEntry(_entries[i], spacing * _entries[i].Slot);
   */
  private advanceLine(): void {
    for (const entry of this.entries) {
      this.retargetEntry(entry, this.tuning.queueRowSpacing * entry.slot)
    }
  }

  /**
   * `PeopleQueue.RetargetEntry`:
   *
   *     if (Mathf.Approximately(entry.Distance, targetDistance)) return;
   *     entry.Tween?.Kill();
   *     float speed = Mathf.Max(0.01f, advanceSpeed);
   *     entry.Character.SetAnimationState(Walking);              // no-op stub
   *     entry.Tween = DOTween.To(() => entry.Distance,
   *                              d => { entry.Distance = d; ApplyPose(entry); },
   *                              targetDistance,
   *                              Mathf.Abs(entry.Distance - targetDistance) / speed)
   *                          .SetEase(...)
   *                          .OnComplete(() => SetAnimationState(Idle));
   *
   * Duration proportional to distance, which is what makes `advanceSpeed` mean
   * "world units per second" — so the ease is left linear here, unlike the
   * `Character` wobbles. Anything else would contradict the tooltip.
   *
   * The early-out is live, and it used to fire too often: with `FLT_EPSILON` in
   * `Mathf.Approximately`'s floor the threshold never drops below 9.54e-7, so a
   * walker who was all but on their mark had their retarget skipped. Over the
   * 40 levels that is **703 / 1 241 786** calls decided the wrong way — always
   * the same shape, `AdvanceLine` re-aiming a front-row entry at 0 while it is
   * a hair short of 0. The residue is float64-small here; in the shipped
   * float32 build the same expression bottoms out around `1.4 * 2^-24` ≈ 8.3e-8,
   * still under that floor, so the branch is reachable there too.
   *
   * 🔍 And the early-out is a **bare `return`**. In `__0x2120038.c` the whole
   * body of the branch is
   *
   *     if (ABS(param_1 - fVar10) < fVar8) { return; }
   *     lVar3 = *plVar6;                            // ← Kill() only starts here
   *
   * so the running tween is *not* killed and `entry.Distance`'s destination is
   * *not* moved: a walker already on their mark is left entirely alone. This
   * port used to write `duration = 0` and `targetDistance = targetDistance`
   * first, which cancels the tween an entry is in the middle of.
   *
   * That is bounded rather than merely rare, which is why it survived: the
   * early-out can only catch an in-flight tween within `1e-06 * magnitude` of
   * that tween's *own* destination, because `AdvanceLine` re-aims an entry at
   * `spacing * Slot` — the very point it is already walking to. Measured over
   * the 40 levels: 37 239 of 360 162 early-outs did land on an entry with a
   * tween running, but the furthest any of them still had to go was
   * **7.1e-15** units. The wrong writes cost nothing observable today; they are
   * removed because the decomp says so, not because a level noticed.
   */
  private retargetEntry(entry: QueueEntry, targetDistance: number): void {
    if (approximately(entry.distance, targetDistance)) return
    const speed = Math.max(0.01, this.tuning.queueAdvanceSpeed)
    entry.fromDistance = entry.distance
    entry.targetDistance = targetDistance
    entry.elapsed = 0
    entry.duration = Math.abs(entry.distance - targetDistance) / speed
  }

  /**
   * `PeopleQueue.FillFromPending` — keep the visible line topped up.
   *
   *     while (_entries.Count < MaxVisible) {
   *         if (_pendingColors.Count < 1) return;
   *         var color = _pendingColors.Dequeue();
   *         int lane = 0, slot = 0;
   *         if (_entries.Count > 0) {
   *             var last = _entries[^1];
   *             int inRow = _entries.Count(e => e.Slot == last.Slot);
   *             if (inRow < laneCount) { slot = last.Slot; lane = inRow; }
   *             else                   { slot = last.Slot + 1; lane = 0; }
   *         }
   *         float target = spacing * slot;
   *         float start  = target;
   *         if (!instant) {
   *             float behind = _entries.Count < 1
   *                 ? target + spacing
   *                 : _entries[^1].Distance + (_entries[^1].Slot != slot ? spacing : 0f);
   *             start = Mathf.Max(_pathLength, behind);
   *         }
   *         ... entry.Distance = start; ApplyPose(entry); _entries.Add(entry);
   *         if (!instant) { DOScale(0 -> scale, 0.179f); RetargetEntry(entry, target); }
   *     }
   *
   * The `instant` flag is what separates the two call sites: `Initialize`
   * inlines `FillFromPending(true)` and drops everyone straight onto their row,
   * while `TakeFront` calls `FillFromPending(false)` so a replacement appears
   * **at the far end of the path** — never nearer than `_pathLength` — and
   * walks up. On a line that is already `MaxVisible` long the two are almost
   * the same (the back row sits at ~33.6 and the path is 33.9 long), but on a
   * short line it is the difference between the queue popping into existence
   * and it filling from the back.
   */
  private fillFromPending(instant = false): void {
    const lanes = this.laneCount
    const spacing = this.tuning.queueRowSpacing
    const maxVisible = this.maxVisible
    while (this.entries.length < maxVisible) {
      if (this.pending.length < 1) return
      const color = this.pending.shift()!

      let slot = 0
      let lane = 0
      if (this.entries.length > 0) {
        const last = this.entries[this.entries.length - 1]
        const inRow = this.entries.filter((e) => e.slot === last.slot).length
        if (inRow < lanes) {
          slot = last.slot
          lane = inRow
        } else {
          slot = last.slot + 1
          lane = 0
        }
      }

      const target = spacing * slot
      // Read the tail *before* pushing: the original computes this from the
      // previous last entry, not from the one it is about to add.
      let start = target
      if (!instant) {
        const behind =
          this.entries.length < 1
            ? target + spacing
            : this.entries[this.entries.length - 1].distance +
              (this.entries[this.entries.length - 1].slot !== slot ? spacing : 0)
        start = Math.max(this.pathLength, behind)
      }

      const entry: QueueEntry = {
        color,
        distance: start,
        targetDistance: start,
        fromDistance: start,
        elapsed: 0,
        duration: 0,
        slot,
        lane,
      }
      this.entries.push(entry)
      if (!instant) this.retargetEntry(entry, target)
    }
  }

  /** Run the per-entry retarget tweens. */
  update(dt: number): void {
    for (const entry of this.entries) {
      if (entry.duration <= 0) continue
      entry.elapsed += dt
      if (entry.elapsed >= entry.duration) {
        entry.distance = entry.targetDistance
        entry.duration = 0
        continue
      }
      const t = entry.elapsed / entry.duration
      entry.distance = entry.fromDistance + (entry.targetDistance - entry.fromDistance) * t
    }
  }
}

const _ahead = new Vector3()
const _behind = new Vector3()
const _right = new Vector3()
const _pose = { position: new Vector3(), forward: new Vector3() }

function catmullRom(p0: Vector3, p1: Vector3, p2: Vector3, p3: Vector3, t: number): Vector3 {
  const t2 = t * t
  const t3 = t2 * t
  return new Vector3(
    0.5 * (2 * p1.x + (-p0.x + p2.x) * t + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
    0.5 * (2 * p1.y + (-p0.y + p2.y) * t + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3),
    0.5 * (2 * p1.z + (-p0.z + p2.z) * t + (2 * p0.z - 5 * p1.z + 4 * p2.z - p3.z) * t2 + (-p0.z + 3 * p1.z - 3 * p2.z + p3.z) * t3),
  )
}
